import React, { ComponentType, useEffect, useRef, useState } from "react";
import "./StackedCarousel.css";

interface StackedCarouselProps<T> {
  readonly data: readonly T[];
  readonly slideComponent: ComponentType<SlideComponentProps<T>>;
  readonly slideWidth?: number | string;
  readonly slideScales?: readonly [number, number, number];
  readonly transitionTime?: number;
  readonly sideSlideVisibility?: number;
  readonly overlayColor?: string;
  readonly customOverlays?: readonly [number, number, number];
  readonly slideClassName?: string;
  readonly slideContainerClassName?: string;
  readonly minHeight?: number | string;
  readonly autoHeight?: boolean;
  readonly swipeSensitivity?: number; // 0-1, higher = easier to swipe
}

interface SlideComponentProps<T> {
  data: T;
  dataIndex: number;
}

interface SwipeState {
  startX: number;
  currentX: number;
  isDragging: boolean;
  startTime: number;
  velocityX: number;
  hasMoved: boolean;
}
function StackedCarouselComponent<T>({
  data,
  slideComponent: SlideComponent,
  slideWidth = "65%",
  slideScales = [1, 0.95, 0.9],
  transitionTime = 150,
  sideSlideVisibility = 23,
  overlayColor = "rgb(0, 0, 0)",
  customOverlays = [0, 0.15, 0.3],
  slideClassName = "",
  slideContainerClassName = "rounded-[10px] shadow-[0px_10px_12px_6px_rgba(0,0,0,0.05)]",
  minHeight = "300px",
  autoHeight = true,
  swipeSensitivity = 0.25,
}: StackedCarouselProps<T>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const swipeStateRef = useRef<SwipeState | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const slideRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [computedSlideWidth, setComputedSlideWidth] = useState(0);
  const [computedSlideHeight, setComputedSlideHeight] = useState(0);
  const [isPreventingClicks, setIsPreventingClicks] = useState(false);

  useEffect(() => {
    const updateSlideDimensions = () => {
      let maxHeight = 0;
      let maxWidth = 0;

      slideRefs.current.forEach((slideElement) => {
        if (slideElement) {
          const width = slideElement.offsetWidth;
          const height = slideElement.offsetHeight;
          if (width > maxWidth) {
            maxWidth = width;
          }
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      });

      if (maxWidth > 0) {
        setComputedSlideWidth(maxWidth);
      }
      if (maxHeight > 0) {
        setComputedSlideHeight(maxHeight);
      }
    };

    updateSlideDimensions();
    window.addEventListener("resize", updateSlideDimensions);

    const timer = setTimeout(updateSlideDimensions, 50);

    return () => {
      window.removeEventListener("resize", updateSlideDimensions);
      clearTimeout(timer);
    };
  }, [slideWidth, currentIndex, data]);

  const getDragProgress = (offset: number) => {
    const effectiveWidth =
      computedSlideWidth ||
      (typeof slideWidth === "number" ? slideWidth : window.innerWidth * 0.5);
    return -offset / (effectiveWidth * 0.8);
  };

  const getSlideTransform = (slideIndex: number, dragProgress: number) => {
    const relativePosition = slideIndex - currentIndex - dragProgress;
    const absPos = Math.abs(relativePosition);

    const visibilityPercent = sideSlideVisibility / 100;
    const baseTranslate = 100 * visibilityPercent;
    const isScalingDisabled =
      slideScales[0] === slideScales[1] && slideScales[1] === slideScales[2];

    const translateXPercent = relativePosition * baseTranslate;
    const zIndex = Math.round(100 - absPos * 20);

    let scale: number;
    let overlayAlpha: number;
    let opacity: number;
    let translateZ: number;

    if (absPos <= 1) {
      // Center to first side
      const t = absPos;
      scale = slideScales[0] + (slideScales[1] - slideScales[0]) * t;
      overlayAlpha =
        customOverlays[0] + (customOverlays[1] - customOverlays[0]) * t;
      opacity = 1;
      translateZ = isScalingDisabled ? 0 : -50 * t;
    } else if (absPos <= 2) {
      // First side to second side
      const t = absPos - 1;
      scale = slideScales[1] + (slideScales[2] - slideScales[1]) * t;
      overlayAlpha =
        customOverlays[1] + (customOverlays[2] - customOverlays[1]) * t;
      opacity = 1;
      translateZ = isScalingDisabled ? 0 : -50 - 50 * t;
    } else if (absPos <= 3) {
      // Fading out
      const t = absPos - 2;
      scale = slideScales[2];
      overlayAlpha = customOverlays[2];
      opacity = Math.max(0, 1 - t);
      translateZ = isScalingDisabled ? 0 : -100;
    } else {
      // Hidden
      scale = slideScales[2];
      overlayAlpha = customOverlays[2];
      opacity = 0;
      translateZ = isScalingDisabled ? 0 : -100;
    }

    return {
      scale,
      opacity,
      translateXPercent,
      translateZ,
      zIndex,
      overlayAlpha,
    };
  };

  // Calculate which slides to render based on current drag state
  const dragProgress = getDragProgress(dragOffset);
  const visibleSlides: Array<{
    data: T;
    dataIndex: number;
    transform: ReturnType<typeof getSlideTransform>;
  }> = [];

  // Render slides around current position (including buffer for smooth fade transitions)
  const startIndex = Math.max(0, currentIndex - 3);
  const endIndex = Math.min(data.length - 1, currentIndex + 4);

  for (let i = startIndex; i <= endIndex; i++) {
    visibleSlides.push({
      data: data[i],
      dataIndex: i,
      transform: getSlideTransform(i, dragProgress),
    });
  }

  const handleSwipeStart = (clientX: number) => {
    swipeStateRef.current = {
      startX: clientX,
      currentX: clientX,
      isDragging: true,
      startTime: Date.now(),
      velocityX: 0,
      hasMoved: false,
    };
    setDragOffset(0);
    setIsPreventingClicks(false);
  };

  const handleSwipeMove = (clientX: number) => {
    if (!swipeStateRef.current?.isDragging) return;

    const prevX = swipeStateRef.current.currentX;
    const currentTime = Date.now();
    const deltaTime = currentTime - swipeStateRef.current.startTime;

    swipeStateRef.current.currentX = clientX;
    const deltaX = clientX - swipeStateRef.current.startX;

    // Detect if user has moved more than a few pixels (threshold for detecting swipe vs click)
    if (Math.abs(deltaX) > 3 && !swipeStateRef.current.hasMoved) {
      swipeStateRef.current.hasMoved = true;
      setIsPreventingClicks(true);
    }

    // Calculate velocity (pixels per millisecond)
    if (deltaTime > 0) {
      swipeStateRef.current.velocityX = ((clientX - prevX) / deltaTime) * 100;
    }

    // Apply resistance at boundaries
    const atStart = currentIndex === 0 && deltaX > 0;
    const atEnd = currentIndex === data.length - 1 && deltaX < 0;
    const offset = atStart || atEnd ? deltaX * 0.3 : deltaX;

    setDragOffset(offset);
  };

  const handleSwipeEnd = (clientX: number) => {
    if (!swipeStateRef.current?.isDragging) return;

    const deltaX = clientX - swipeStateRef.current.startX;
    const velocity = swipeStateRef.current.velocityX;
    const dragProgress = getDragProgress(deltaX);

    // Calculate final position based on drag distance and velocity
    // Adjust thresholds based on swipeSensitivity (0-1 scale)
    // Higher sensitivity = easier to swipe (lower thresholds)
    const sensitivity = Math.max(0, Math.min(1, swipeSensitivity));

    // Distance threshold for normal drags: 0.15 (very easy) to 0.7 (very hard)
    const distanceThreshold = 0.7 - 0.55 * sensitivity;

    // Minimum distance required even for velocity-based swipes
    const minDistanceForVelocity = 0.05; // Must drag at least 5% regardless of speed

    // Velocity threshold: 0.5 (very easy) to 3 (very hard)
    const velocityThreshold = 3 - 2.5 * sensitivity;

    let targetOffset = 0;

    // Check if minimum distance is met
    const hasMinDistance = Math.abs(dragProgress) > minDistanceForVelocity;

    // Velocity-based: fast flick with minimum distance
    if (hasMinDistance && Math.abs(velocity) > velocityThreshold) {
      targetOffset = velocity < 0 ? 1 : -1;
    }
    // Distance-based: crossed the threshold
    else if (Math.abs(dragProgress) > distanceThreshold) {
      targetOffset = Math.round(dragProgress);
    }
    // Too small movement - stay on current slide (targetOffset remains 0)

    // Clamp to valid range
    const targetIndex = Math.max(
      0,
      Math.min(data.length - 1, currentIndex + targetOffset)
    );

    setDragOffset(0);

    // Only transition if we're changing slides
    if (targetIndex !== currentIndex) {
      setCurrentIndex(targetIndex);
    }

    // If user swiped, keep preventing clicks briefly
    const hasSwiped = swipeStateRef.current.hasMoved;
    swipeStateRef.current = null;

    if (!hasSwiped) {
      setIsPreventingClicks(false);
    } else {
      // Keep preventing clicks for a short time after swipe ends
      setTimeout(() => {
        setIsPreventingClicks(false);
      }, 100);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) =>
    handleSwipeStart(e.touches[0].clientX);

  const handleTouchMove = (e: React.TouchEvent) =>
    handleSwipeMove(e.touches[0].clientX);

  const handleTouchEnd = (e: React.TouchEvent) =>
    handleSwipeEnd(e.changedTouches[0].clientX);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSwipeStart(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!swipeStateRef.current?.isDragging) return;
    e.preventDefault();
    handleSwipeMove(e.clientX);
  };

  const handleMouseUp = (e: React.MouseEvent) => handleSwipeEnd(e.clientX);

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (swipeStateRef.current?.isDragging) {
      handleSwipeEnd(e.clientX);
    }
  };

  const slideWidthCSS =
    typeof slideWidth === "number" ? `${slideWidth}px` : slideWidth;

  const isScalingDisabled =
    slideScales[0] === slideScales[1] && slideScales[1] === slideScales[2];

  const handleClick = (e: React.MouseEvent) => {
    // Prevent clicks if we're in the middle of or just finished a swipe
    if (isPreventingClicks) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (currentIndex < data.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    } else if (e.key === "Home") {
      e.preventDefault();
      setCurrentIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setCurrentIndex(data.length - 1);
    }
  };

  // Calculate minHeight based on autoHeight and minHeight prop
  const calculateMinHeight = () => {
    if (autoHeight && computedSlideHeight > 0) {
      return `${computedSlideHeight}px`;
    }
    if (typeof minHeight === "number") {
      return `${minHeight}px`;
    }
    return minHeight;
  };

  return (
    <div
      className="stacked-carousel-container relative flex w-full items-center justify-center"
      role="region"
      aria-roledescription="carousel"
      aria-label="Image carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onClickCapture={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        minHeight: calculateMinHeight(),
        overflow: "visible",
        transition:
          autoHeight && computedSlideHeight > 0
            ? "min-height 0.3s ease"
            : "none",
        touchAction: "pan-y pinch-zoom",
      }}
    >
      <div
        className="relative flex items-center justify-center"
        style={{
          width: "100%",
          height: "100%",
          perspective: isScalingDisabled ? "none" : "1000px",
        }}
      >
        {visibleSlides.map((slide) => (
          <div
            key={slide.dataIndex}
            ref={(el) => {
              if (el) {
                slideRefs.current.set(slide.dataIndex, el);
              } else {
                slideRefs.current.delete(slide.dataIndex);
              }
            }}
            className={`stacked-carousel-slide absolute ${slideClassName}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${slide.dataIndex + 1} of ${data.length}`}
            aria-hidden={slide.dataIndex !== currentIndex}
            style={{
              width: slideWidthCSS,
              height:
                autoHeight && computedSlideHeight > 0
                  ? `${computedSlideHeight}px`
                  : "auto",
              zIndex: slide.transform.zIndex,
              transform: `translateX(${slide.transform.translateXPercent}%) translateZ(${slide.transform.translateZ}px) scale(${slide.transform.scale})`,
              opacity: slide.transform.opacity,
              transitionDuration:
                dragOffset !== 0 ? "0ms" : `${transitionTime}ms`,
              transitionProperty: "transform, opacity",
              transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div
              className={`relative h-full w-full overflow-hidden ${slideContainerClassName}`}
            >
              <SlideComponent data={slide.data} dataIndex={slide.dataIndex} />
              {slide.transform.overlayAlpha > 0 && (
                <div
                  className="pointer-events-none absolute inset-0 rounded-[inherit]"
                  style={{
                    backgroundColor: overlayColor,
                    opacity: slide.transform.overlayAlpha,
                    transitionDuration:
                      dragOffset !== 0 ? "0ms" : `${transitionTime}ms`,
                    transitionProperty: "opacity",
                    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const StackedCarousel = React.memo(
  StackedCarouselComponent
) as typeof StackedCarouselComponent;
